import React from "react";
import { ErrorBoundary, type ErrorBoundaryProps, type FallbackProps } from "react-error-boundary";
import { FormattedMessage } from "react-intl";

import {
  isChunkLoadError,
  isChunkLoadSyntaxError,
  recoverFromChunkLoadError,
} from "~/common/chunk-load-recovery";
import { remoteLog } from "~/common/logging";
import { Portlet, PortletTitle } from "~/components/ui/portlet";

export function RemoteLogErrorBoundary({
  component,
  children,
  fallbackRender = portletFallbackRender,
  onReset,
}: {
  component: string;
  children: React.ReactNode;
  fallbackRender?: ErrorBoundaryProps["fallbackRender"] | null;
  onReset?: ErrorBoundaryProps["onReset"];
}) {
  return (
    <ErrorBoundary
      fallbackRender={fallbackRender ?? nullRender}
      onError={(err) => {
        // Recover from "Loading chunk X failed" after a deploy.
        if (isChunkLoadError(err) || isChunkLoadSyntaxError(err)) {
          recoverFromChunkLoadError();
        }
        remoteLog(err, component);
      }}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
}

function portletFallbackRender(_: FallbackProps) {
  return (
    <Portlet>
      <PortletTitle iconClassName="icon-info">
        <FormattedMessage id="error_boundary.title" />
      </PortletTitle>

      <p role="alert" className="tw-space-y-4">
        <span className="tw-block">
          <FormattedMessage id="error_boundary.message" />.
        </span>

        <span className="tw-block">
          <FormattedMessage id="error_boundary.message_2" />.
        </span>
      </p>
    </Portlet>
  );
}

function nullRender(_: FallbackProps) {
  return null;
}
